import { ColumnDef } from "@tanstack/react-table";
import { VisibleFeature, QIVisibleFeatures } from "../../types/types";

export interface QueueIsoProperties {
    // Visible Properties
    Zone: string;
    "Size (MW)": number;
    Projects?: number;
    "Fuel Type"?: string;
    "Sub Rows"?: Array<{
        Type: string;
        "Size (MW)": number;
        Projects: number;
    }>;

    // Internal Properties
    ISO: string;
    "Queue Status": string;
}

export interface QueueIsoFeature extends VisibleFeature {
    properties: QueueIsoProperties;
}

export function isQueueIsoFeature(
    feature: VisibleFeature
): feature is QueueIsoFeature {
    return feature.type === QIVisibleFeatures.iso && "properties" in feature;
}

export interface QueueIsoDisplay {
    // Visible Properties
    name: string;
    mw: number;
    projects: number;
    subRows?: QueueIsoDisplay[];
    fuelType: string;

    // Internal Properties
    iso: string;
    queueStatus: string;
}

// Helper function to transform feature data into display format
export function transformQueueIsoFeature(
    feature: QueueIsoFeature
): QueueIsoDisplay {
    const mainRow: QueueIsoDisplay = {
        name: feature.properties["Zone"] || "N/A",
        mw: feature.properties["Size (MW)"] || 0,
        projects: feature.properties["Projects"] || 1,
        fuelType: feature.properties["Fuel Type"] || "Unknown",
        iso: feature.properties["ISO"] || "Unknown",
        queueStatus: feature.properties["Queue Status"] || "Unknown",
    };

    //Add subrows
    if (
        feature.properties["Sub Rows"] &&
        feature.properties["Sub Rows"].length > 0
    ) {
        mainRow.subRows = feature.properties["Sub Rows"].map((fuelType) => ({
            name: fuelType["Type"] || "Unknown",
            mw: fuelType["Size (MW)"] || 0,
            projects: fuelType["Projects"] || 0,
            fuelType: fuelType["Type"] || "Unknown",
            iso: feature.properties["ISO"] || "Unknown",
            queueStatus: feature.properties["Queue Status"] || "Unknown",
        }));
    }

    return mainRow;
}

export const queueIsoColumns: ColumnDef<QueueIsoDisplay>[] = [
    {
        accessorKey: "name",
        header: "Zone",
        enableSorting: true,
        cell: ({ row, getValue }) => {
            const isParentRow = row.getCanExpand();
            const value = getValue() as string;

            return (
                <div className="flex items-center">
                    <span
                        className={
                            isParentRow
                                ? "font-bold text-black dark:text-white uppercase"
                                : ""
                        }
                    >
                        {value}
                    </span>
                </div>
            );
        },
    },
    {
        accessorKey: "mw",
        header: "MW",
        enableSorting: true,
        cell: ({ getValue }) => {
            return (
                <div className="text-right w-full">
                    {(getValue() as number).toFixed(2)} MW
                </div>
            );
        },
    },
    {
        accessorKey: "projects",
        header: "Projects",
        enableSorting: true,
        cell: ({ getValue }) => {
            return (
                <div className="text-right w-full">{getValue() as number}</div>
            );
        },
    },
];
